import React from "react";
import clsx from "clsx";

import Modal from "../../../atoms/Modal";
import AnswerList from "../AnswerList";
import {
  Context,
  updateComment,
  createComment,
  deleteComment,
  toggleComment,
  fetchAnswers,
  fetchStudents,
  fetchGroups,
  fetchClasses,
  answerOpened,
  answerClosed,
  nameOpened,
  nameClosed,
  fetchAnswersFromOtherSchools,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";

import styles from "./ModalAnswers.module.scss";

export type Props = {
  issueID: number;
  classIDs: number[];
  is_display_all_schools_answer: boolean;
  isMaterial?: boolean;
  onClose: () => void;
};

const ModalAnswers: React.FC<Props> = ({
  issueID,
  classIDs,
  is_display_all_schools_answer,
  isMaterial = false,
  onClose,
}) => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const classIDsStr = classIDs.join(",");
  const clsIDs = React.useMemo(
    () => classIDsStr.split(",").map(s => Number(s)),
    [classIDsStr],
  );

  const refreshAnswers = React.useCallback(() => {
    issueID && dispatch(fetchAnswers(issueID, clsIDs));
  }, [clsIDs, dispatch, issueID]);

  const answersWithIndex = React.useMemo(() => {
    return state.answersByClasses.map(answersByClass => {
      const answers = answersByClass.answers.map((answer, index) => {
        return { ...answer, index: index + 1 };
      });
      return { ...answersByClass, answers };
    });
  }, [state.answersByClasses]);

  React.useEffect(() => {
    if (is_display_all_schools_answer) {
      dispatch(fetchAnswersFromOtherSchools(issueID, clsIDs));
    }
  }, [clsIDs, dispatch, is_display_all_schools_answer, issueID]);

  React.useEffect(() => {
    dispatch(fetchStudents(clsIDs));
    dispatch(fetchGroups(clsIDs));
    dispatch(fetchClasses());
  }, [clsIDs, dispatch]);

  React.useEffect(() => {
    refreshAnswers();
    const refetchInterval = setInterval(() => refreshAnswers(), 10000);
    return () => clearInterval(refetchInterval);
  }, [refreshAnswers]);

  const changeAnswerOpenClose = React.useCallback(
    (class_id: number, opened: boolean) => {
      if (opened) {
        dispatch(answerOpened(issueID, class_id));
      } else {
        dispatch(answerClosed(issueID, class_id));
      }
    },
    [dispatch, issueID],
  );

  const changeNameOpenClose = React.useCallback(
    (class_id: number, opened: boolean) => {
      if (opened) {
        dispatch(nameOpened(issueID, class_id));
      } else {
        dispatch(nameClosed(issueID, class_id));
      }
    },
    [dispatch, issueID],
  );

  const commentFunc = React.useCallback(
    (content: string, answer_id: number, comment_id?: number) => {
      comment_id
        ? dispatch(peep(updateComment(answer_id, content, comment_id)))
        : dispatch(peep(createComment(answer_id, content)));
    },
    [dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (answer_id: number, comment_id: number) => {
      dispatch(peep(deleteComment(answer_id, comment_id)));
    },
    [dispatch, peep],
  );

  const toggleCommentFunc = React.useCallback(
    (answer_id: number, comment_id: number, is_hidden: boolean) => {
      dispatch(peep(toggleComment(answer_id, comment_id, is_hidden)));
    },
    [dispatch, peep],
  );

  return (
    <Modal
      className={clsx(styles.modal, isMaterial && "ms-0")}
      onClose={onClose}
      transparentBackDrop={true}
    >
      <div className={styles.modal__content}>
        <AnswerList
          calling={calling}
          currentTeacherID={state.teacher.id}
          students={state.students}
          answersByClasses={answersWithIndex}
          answersOtherSchoolsByClasses={state.answersOtherSchoolsByClasses}
          classes={state.classes}
          classIDs={clsIDs}
          groups={state.groups}
          is_display_all_schools_answer={is_display_all_schools_answer}
          handleComment={commentFunc}
          handleDeleteComment={deleteCommentFunc}
          handleToggleComment={toggleCommentFunc}
          changeAnswerOpenClose={changeAnswerOpenClose}
          changeNameOpenClose={changeNameOpenClose}
        />
      </div>
    </Modal>
  );
};

export default ModalAnswers;
